import { useApolloClient } from '@apollo/client'
import getProductsInCategory from '../../graphql/getProductsInCategory'
import getProductDetails from '../../graphql/getProductDetails'
import { useState, useEffect, useCallback } from 'react'

// react-apollo useLazyQuery doesn't return a promise
// This is a work-around
export function useLazyQuery(query) {
  const client = useApolloClient()
  return useCallback(
    (urlKey) =>
      client.query({
        query,
        variables: {
          urlKey,
        },
      }),
    [client],
  )
}

export function useGiftBundles() {
  const GIFT_BUNDLES = 'categories/gift-bundles'
  const MINI_BUNDLES = 'categories/mini-bundles'

  const [allBundles, setAllBundles] = useState([])
  const [miniBundles, setMiniBundles] = useState([])
  const [allBundlesLoading, setAllLoading] = useState(true)
  const [miniBundlesLoading, setMiniLoading] = useState(true)
  const [bundlesError, setBundlesError] = useState(false)

  const getCategory = useLazyQuery(getProductsInCategory)
  const getDetails = useLazyQuery(getProductDetails)

  async function fetchByCategory(urlKey) {
    return await getCategory(urlKey)
      .then(({ data }) => {
        return data.getProductsInCategory.all_a_z
      })
      .catch((error) => setBundlesError(error))
  }

  async function fetchItemDetails(urlKey) {
    return await getDetails(urlKey)
      .then(({ data }) => data.getProductDetails)
      .catch((error) => setBundlesError(error))
  }

  async function forEachBundle(bundles, fn) {
    await Promise.all(bundles.map(fn))
  }

  async function fetchAllBundles() {
    await fetchByCategory(GIFT_BUNDLES).then((bundlesInCategory) =>
      forEachBundle(bundlesInCategory, async (bundle) => {
        await fetchItemDetails(bundle.url_key)
          .then((productDetails) =>
            scaffoldBundleItem(
              productDetails,
              bundle.url_key,
              bundle.categories,
            ),
          )
          .then((bundles) => setAllBundles((prev) => [...prev, ...bundles]))
          .catch((error) => setBundlesError(error))
      }),
    )
  }

  async function fetchMiniBundles() {
    await fetchByCategory(MINI_BUNDLES).then((bundlesInCategory) =>
      forEachBundle(bundlesInCategory, async (bundle) => {
        await fetchItemDetails(bundle.url_key)
          .then((productDetails) =>
            scaffoldBundleItem(
              productDetails,
              bundle.url_key,
              bundle.categories,
            ),
          )
          .then((bundles) => setMiniBundles((prev) => [...prev, ...bundles]))
          .catch((error) => setBundlesError(error))
      }),
    )
  }

  function scaffoldBundleItem(data, via, categories) {
    const { name, description, terms_and_conditions, img_url, variants } = data

    return variants.map((variant) => {
      const { sku, price, promotions, children } = variant

      let bundle_img_url = img_url

      if (variant.img_url) bundle_img_url = variant.img_url

      return {
        name,
        description,
        terms_and_conditions,
        img_url, // fallback
        bundle_img_url, // main image
        sku,
        price,
        promotions,
        url_key: via,
        children,
        categories: categories || [],
      }
    })
  }

  useEffect(() => {
    fetchAllBundles().then(() => setAllLoading(false))
  }, [])

  useEffect(() => {
    fetchMiniBundles().then(() => setMiniLoading(false))
  }, [])

  return {
    allBundles,
    allBundlesLoading,
    miniBundles,
    miniBundlesLoading,
    bundlesError,
  }
}
